import { ActionIcon, Tooltip } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react"
import dayjs from "dayjs"
import { DateOnly } from "../../../../../@client/System"
import { useTranslation } from "react-i18next"
import { dateAndTimeOnlyToDateTime } from "../../../../../utils/date-only-to-date-time"
import { mapNullable } from "../../../../../utils/nullable"
import { ResponsiveActionIconButton } from "./responsive-action-icon-button"
import { dateToDateOnly } from "@/utils/date-to-date-only"
import {
  dayJstoDateOnly,
  isFirstDayOfMonth,
  isLastDayOfMonth,
} from "@/utils/dayjs-to-date-only"

const diffInDays = (startDate: Date, endDate: Date) =>
  dayjs(endDate).diff(dayjs(startDate), "day") + 1

export function PeriodSelect({
  value,
  onChange,
}: {
  value: [DateOnly | null, DateOnly | null]
  onChange: (value: [DateOnly | null, DateOnly | null]) => void
}) {
  const startDate = mapNullable(value[0], (v) =>
    dayjs(dateAndTimeOnlyToDateTime(v, "00:00:00")).toDate()
  )
  const endDate = mapNullable(value[1], (v) =>
    dayjs(dateAndTimeOnlyToDateTime(v, "23:59:59")).toDate()
  )

  function set(v: [Date | null, Date | null]) {
    onChange([
      mapNullable(v[0], dateToDateOnly),
      mapNullable(v[1], dateToDateOnly),
    ])
  }

  function moveSelectedDateRange(direction: "forward" | "backward") {
    if (startDate === null || endDate === null) {
      return
    }
    const diff = diffInDays(startDate, endDate)
    const count = direction === "forward" ? diff : -diff

    set([
      dayjs(startDate).add(count, "day").toDate(),
      dayjs(endDate).add(count, "day").toDate(),
    ])
  }

  function filterToday() {
    const today = dayjs()
    const diff =
      startDate && endDate ? dayjs(endDate).diff(dayjs(startDate), "day") : 1
    set([today.toDate(), today.add(diff, "day").toDate()])
  }
  const { t } = useTranslation("app_server")
  return (
    <>
      <DatePickerInput
        presets={[
          {
            label: t("LastWeek"),
            value: [
              dayjs().startOf("week").subtract(1, "week").format("YYYY-MM-DD"),
              dayjs().endOf("week").subtract(1, "week").format("YYYY-MM-DD"),
            ],
          },
          {
            label: t("CurrentWeek"),
            value: [
              dayjs().startOf("week").format("YYYY-MM-DD"),
              dayjs().endOf("week").format("YYYY-MM-DD"),
            ],
          },
          {
            label: t("NextWeek"),
            value: [
              dayjs().startOf("week").add(1, "week").format("YYYY-MM-DD"),
              dayjs().endOf("week").add(1, "week").format("YYYY-MM-DD"),
            ],
          },
          {
            label: t("TwoWeeks"),
            value: [
              dayjs().startOf("week").format("YYYY-MM-DD"),
              dayjs().endOf("week").add(1, "week").format("YYYY-MM-DD"),
            ],
          },
          {
            label: t("CurrentMonth"),
            value: [
              dayjs().startOf("month").format("YYYY-MM-DD"),
              dayjs().endOf("month").format("YYYY-MM-DD"),
            ],
          },
          {
            label: t("NextMonth"),
            value: [
              dayjs().startOf("month").add(1, "month").format("YYYY-MM-DD"),
              dayjs().endOf("month").add(1, "month").format("YYYY-MM-DD"),
            ],
          },
          {
            label: t("LastMonth"),
            value: [
              dayjs()
                .startOf("month")
                .subtract(1, "month")
                .format("YYYY-MM-DD"),
              dayjs().endOf("month").subtract(1, "month").format("YYYY-MM-DD"),
            ],
          },
        ]}
        leftSection={
          <ActionIcon
            size="sm"
            variant="subtle"
            color="dimmed"
            onClick={() => moveSelectedDateRange("backward")}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
        }
        rightSection={
          <ActionIcon
            size="sm"
            variant="subtle"
            color="dimmed"
            onClick={() => {
              moveSelectedDateRange("forward")
            }}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        }
        size="xs"
        dropdownType="modal"
        highlightToday
        valueFormat="L"
        type="range"
        value={[startDate, endDate]}
        onChange={(v) => {
          set([v[0] ? new Date(v[0]) : null, v[1] ? new Date(v[1]) : null])
        }}
      />
      <Tooltip label={t("Today")} openDelay={350}>
        <ResponsiveActionIconButton
          actionIconProps={{
            variant: "default",
            onClick: () => filterToday(),
          }}
          buttonProps={{
            variant: "default",
            size: "xs",
            children: t("Today"),
            onClick: () => filterToday(),
          }}
          icon={<IconCalendar size={16} />}
        />
      </Tooltip>
    </>
  )
}

export function DatePeriodSelect({
  value,
  onChange,
}: {
  value: [DateOnly | null, DateOnly | null]
  onChange: (value: [DateOnly | null, DateOnly | null]) => void
}) {
  const startDate = value[0]
  const endDate = value[1]

  function moveSelectedDateRange(direction: "forward" | "backward") {
    if (startDate === null || endDate === null) {
      return
    }
    const start = dayjs(startDate)
    const end = dayjs(endDate)
    if (isFirstDayOfMonth(start) && isLastDayOfMonth(end)) {
      // shift one month
      onChange([
        dayJstoDateOnly(end.add(1, "day")),
        dayJstoDateOnly(end.add(1, "day").endOf("month")),
      ])
    } else {
      const diff = end.diff(start) * (direction === "forward" ? 1 : -1)
      const shift = direction === "forward" ? 1 : -1

      onChange([
        dayJstoDateOnly(start.add(diff).add(shift, "day")),
        dayJstoDateOnly(end.add(diff).add(shift, "day")),
      ])
    }
  }

  function filterToday() {
    const today = dayjs()
    const diff =
      startDate && endDate ? dayjs(endDate).diff(dayjs(startDate), "day") : 1
    onChange([
      dateToDateOnly(today.toDate()),
      dateToDateOnly(today.add(diff, "day").toDate()),
    ])
  }
  const { t } = useTranslation("app_server")
  return (
    <>
      <DatePickerInput
        leftSection={
          <ActionIcon
            size="sm"
            variant="subtle"
            color="dimmed"
            onClick={() => moveSelectedDateRange("backward")}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
        }
        rightSection={
          <ActionIcon
            size="sm"
            variant="subtle"
            color="dimmed"
            onClick={() => {
              moveSelectedDateRange("forward")
            }}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        }
        size="xs"
        dropdownType="modal"
        highlightToday
        valueFormat="L"
        type="range"
        value={value}
        onChange={(v) => onChange(v as [DateOnly | null, DateOnly | null])}
      />
      <Tooltip label={t("Today")} openDelay={350}>
        <ResponsiveActionIconButton
          actionIconProps={{
            variant: "default",
            onClick: () => filterToday(),
          }}
          buttonProps={{
            variant: "default",
            size: "xs",
            children: t("Today"),
            onClick: () => filterToday(),
          }}
          icon={<IconCalendar size={16} />}
        />
      </Tooltip>
    </>
  )
}
